import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import { useLocation, useNavigate } from 'react-router-dom';
import { GET_DETALLE_PRECIO_MASIVO } from '../../../data/graphQL/queries/GestionPrecioMasivo/getDetallePrecioMasivo';
import { GET_ALL_USUARIO_SOLICITANTE } from '../../../data/graphQL/queries/GestionPrecioMasivo/getAllUsuarioSolicitante';
import { GET_ALL_USUARIO_AUTORIZADOR } from '../../../data/graphQL/queries/GestionPrecioMasivo/getAllUsuarioAutorizador';
import { PROCESAR_PRECIO_MASIVO } from '../../../data/graphQL/mutations/GestionPrecioMasivo/procesarPrecioMasivo';
import { ELIMINAR_PRECIO_MASIVO } from '../../../data/graphQL/mutations/GestionPrecioMasivo/eliminarPrecioMasivo';
import { adaptPrecioDetalleMasivoArray } from '../../../adapters/precioMasivoDetalleAdapter';
import { CustomChip } from '../../../components/chip/Chip';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { TextInput } from '../../../components/textInput/TextInput';
import './gestionPrecioMasivoDetalle.scss';
import { Box, Grid } from '@mui/material';
import { DropdownContent } from '../../../components/dropdownContent/DropdownContent';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { SelectInput } from '../../../components/selectInput/SelectInput';
import { adaptAllUsuarioAutorizadorArray } from '../../../adapters/allUsuarioAutorizdorAdapter';
import { adaptAllUsuarioSolicitanteArray } from '../../../adapters/allUsuarioSolicitanteAdapter';
import toast from 'react-hot-toast';
import { formatCurrency } from "../../../utils/mathUtils";

const locale = "es-ES";

export const GestionPrecioMasivoDetalle = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { precio, allowedWrite } = state;
  const [estaModificado] = useState(precio.estado == 'Modificado');
  const [precioDetalle, setPrecioDetalle] = useState(null);
  const [autorizadores, setAutorizadores] = useState([]);
  const [solicitantes, setSolicitantes] = useState([]);
  const idUsuario = parseInt(sessionStorage.getItem("idUsuario"), 10);
  const [dataProcesar, setDataProcesar] = useState({
    idPrecioMasivo: precio.idPreciosMasivos,
    idUsuario: idUsuario,
    idUsuarioAutorizador: precio.idUsuarioAutorizador,
    idUsuarioSolicitante: precio.idUsuarioSolicitante,
    observaciones: precio.observaciones
  });

  const [getPrecioMasivoDetalle, { data: precioData, loading: preciosLoading }] = useLazyQuery(GET_DETALLE_PRECIO_MASIVO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!precioData) return;
    setPrecioDetalle(adaptPrecioDetalleMasivoArray(precioData));
  }, [precioData]);


  const [getUsuariosAutorizadores, { data: dataUsuariosAutorizadores, loading: autorizadoresLoading }] = useLazyQuery(GET_ALL_USUARIO_AUTORIZADOR, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataUsuariosAutorizadores) return;
    setAutorizadores(adaptAllUsuarioAutorizadorArray(dataUsuariosAutorizadores));
  }, [dataUsuariosAutorizadores]);

  const [getUsuariosSolicitantes, { data: dataUsuariosSolicitantes, loading: solicitantesLoading }] = useLazyQuery(GET_ALL_USUARIO_SOLICITANTE, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataUsuariosSolicitantes) return;
    setSolicitantes(adaptAllUsuarioSolicitanteArray(dataUsuariosSolicitantes));
  }, [dataUsuariosSolicitantes]);

  useEffect(() => {
    if (!preciosLoading) {
      getPrecioMasivoDetalle({ variables: { idPrecioMasivo: precio.idPreciosMasivos } });
    }
    if (!autorizadoresLoading) {
      getUsuariosAutorizadores();
    }
    if (!solicitantesLoading) {
      getUsuariosSolicitantes();
    }
  }, []);

  const getChipEstado = (estado) => {
    switch (estado) {
      case "Pendiente":
        return (
          <CustomChip label={estado} isYellow />);
      case "Modificado":
        return (
          <CustomChip label={estado} isGreen />);
      default:
        return ('');
    }
  };

  const initialColumns = [
    {
      field: 'delegacion',
      headerName: 'Delegación',
      flex: 0.35
    },
    {
      field: 'idEvaluacion',
      headerName: 'Contrato',
      flex: 0.2
    },
    {
      field: 'paciente',
      headerName: 'Paciente',
      flex: 0.4
    },
    {
      field: 'cod_Sap',
      headerName: 'Código SAP',
      flex: 0.3
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      flex: 0.5
    },
    {
      field: 'periodo',
      headerName: 'Período',
      flex: 0.2
    },
    {
      field: 'precioDiario',
      headerName: 'Precio Diario',
      flex: 0.3,
      valueGetter: (value) => formatCurrency(value)
    },
    {
      field: 'precioAModificar',
      headerName: 'Nuevo precio',
      flex: 0.3,
      valueGetter: (value) => formatCurrency(value)
    },
    {
      field: 'precioSAP',
      headerName: 'Precio SAP',
      flex: 0.3,
      valueGetter: (value) => formatCurrency(value)
    },
    {
      field: 'tipoPrecio',
      headerName: 'Tipo Precio',
      flex: 0.3
    },
    {
      field: 'precioControl',
      headerName: 'Precio Control',
      flex: 0.3,
      valueGetter: (value) => formatCurrency(value)
    },
    {
      field: 'viable',
      headerName: 'Viable',
      flex: 0.175
    },
    {
      field: 'gerenteO',
      flex: 0.25,
      align: 'center',
      headerName: 'GO',
      headerAlign: 'center',
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.gerenteO);
      }
    },
    {
      field: 'existe',
      headerName: 'existe',
      flex: 0.175
    }];

  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns, true), [allowedWrite, precioDetalle]);

  const validarProcesar = () => {
    if (dataProcesar.idUsuarioAutorizador == null || dataProcesar.idUsuarioAutorizador == "" || dataProcesar.idUsuarioAutorizador == 0) {
      toast.error("Debe ingresar el Autorizador.");
      return false;
    }

    if (dataProcesar.idUsuarioSolicitante == null || dataProcesar.idUsuarioSolicitante == "" || dataProcesar.idUsuarioSolicitante == 0) {
      toast.error("Debe ingresar el Solicitante.");
      return false;
    }

    return true;
  };

  const handleProcesar = () => {
    if (validarProcesar()) {
      procesar({ variables: dataProcesar });
    }
  };

  const [procesar, { error: procesarError, loading: procesarLoading }] = useMutation(PROCESAR_PRECIO_MASIVO, {
    onCompleted: (data) => {
      if (data.procesarPrecioMasivo) {
        toast.success("El proceso se ejecuto en forma satisfactoria.");
        setTimeout(() => navigate('/gestionPrecioMasivo'), 1000);
      } else {
        toast.error("Se produjo un error en el proceso");
      }
    }
  });

  const handleEliminar = () => {
    eliminar({ variables: { idPrecioMasivo: dataProcesar.idPrecioMasivo } });
  };

  const [eliminar, { error: eliminarError, loading: eliminarLoading }] = useMutation(ELIMINAR_PRECIO_MASIVO, {
    onCompleted: (data) => {
      if (data.eliminarPrecioMasivo) {
        toast.success("Se guardo el registro correctamente.");
        setTimeout(() => navigate('/gestionPrecioMasivo'), 1000);
      } else {
        toast.error("Se produjo un error al eliminar el proceso");
      }
    }
  });

  return (
    <>
      <div className='card-container' style={{ marginTop: "-5px" }}>
        <Grid
          container
          justifyContent={'space-between'}
          rowGap={2}
          gap={1}>

          <Grid size={{ xs: 12, md: 12 }}>
            <div className='titulo-seccion'>Detalle de precios</div>
          </Grid>
          <Grid size={{ md: 1 }}>
            <strong>#</strong>
            <br />
            <div className='label'>{precio?.idPreciosMasivos}</div>
          </Grid>
          <Grid size={{ md: 2 }}>
            <strong>Estado</strong>
            <br />
            <div className='label'>{getChipEstado(precio?.estado)}</div>
          </Grid>
          <Grid size={{ md: 2 }}>
            <strong>Fecha Ingreso</strong>
            <br />
            <div className='label'>{moment(precio?.fechaIngreso).format('DD/MM/yyyy HH:mm:ss')}</div>
          </Grid>
          <Grid size={{ md: 3 }}>
            <SelectInput
              disabled={estaModificado}
              options={autorizadores}
              label='Autorizador'
              onChange={(e) => {
                setDataProcesar((previousData) => ({
                  ...previousData,
                  idUsuarioAutorizador: e.target.value
                }));
              }}
              value={dataProcesar?.idUsuarioAutorizador} />

          </Grid>
          <Grid size={{ md: 3 }}>
            <SelectInput
              disabled={estaModificado}
              options={solicitantes}
              label='Solicitante'
              onChange={(e) => {
                setDataProcesar((previousData) => ({
                  ...previousData,
                  idUsuarioSolicitante: e.target.value
                }));
              }}
              value={dataProcesar?.idUsuarioSolicitante} />

          </Grid>
        </Grid>
        <DropdownContent sectionTitle={'Usuario'} className='grid-row'>
          <Grid
            container
            justifyContent={'space-between'}
            gap={1}>

            <Grid size={{ md: 3 }}>
              <strong>Ejecutador</strong>
              <br />
              <div className='label'>{precio?.usuarioEjecutador != null && precio?.usuarioEjecutador != "" ? precio?.usuarioEjecutador : '-'}</div>
            </Grid>
            <Grid size={{ md: 1.9 }}>
              <strong>Fecha Ejecución</strong>
              <br />
              <div className='label'>{precio?.fechaEjecucion != null && precio?.fechaEjecucion != "" ? moment(precio?.fechaEjecucion).format('DD/MM/yyyy HH:mm:ss') : "-"}</div>
            </Grid>
            <Grid size={{ md: 6.3 }}>
              <strong>Observaciones</strong>
              <br />
              <TextInput
                placeholder='Observaciones'
                onChange={(e) => {
                  setDataProcesar((previousData) => ({
                    ...previousData,
                    observaciones: e.target.value
                  }));
                }}
                disabled={estaModificado}
                value={dataProcesar?.observaciones} />

            </Grid>
          </Grid>
        </DropdownContent>
        {
          !estaModificado &&
          <Grid container sx={{ marginTop: '-30px' }}>
            <Grid size={{ md: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 15 }}>
                <CustomButton
                  className='button-eliminar'
                  size='small'
                  disabled={procesarLoading}
                  onClick={handleEliminar}
                  isLoading={eliminarLoading}>

                  Eliminar
                </CustomButton>
                <CustomButton
                  className='button-procesar'
                  size='small'
                  onClick={handleProcesar}
                  isLoading={procesarLoading}
                  disabled={eliminarLoading}>

                  Procesar
                </CustomButton>
              </div>
            </Grid>
          </Grid>
        }

        <Box sx={{ width: '100%' }}>
          <DataGridGenerico
            columns={columns}
            rows={precioDetalle ?? []}
            toolbar={false}
            loading={preciosLoading}
          />
        </Box>

      </div>
    </>);

};